/**
 * The call log, the missed-call queue, and ringing somebody back.
 *
 * The types are the API's own, from the generated schema, like every `api.ts` here.
 */

import { request, type Schema } from '@taxi/api-client';

export type CallSummary = Schema<'CallSummary'>;

/** The queue, and how many are in it whatever this page holds. */
export type MissedCalls = Schema<'MissedCalls'>;

/** Everybody waiting for a car, and how much room is left on the lines. */
export type HeldCalls = Schema<'HeldCalls'>;

/**
 * What became of a caller the console asked to park for a car.
 *
 * `hold` means the API moved them onto the hold extension; `ringBack` means the line is one
 * the firm will not park somebody on — a SIM — so the dispatcher says the firm will ring
 * back and hangs up.
 */
export type HoldDecision = Schema<'HoldDecision'>;

/** A link to one recording that stops working shortly, and how shortly. */
export type RecordingLink = Schema<'RecordingLink'>;

/** One line of a recording's trail: who heard, saved or deleted it, and when. */
export type RecordingAccess = Schema<'RecordingAccessView'>;

export const callsApi = {
  missed: () => request<MissedCalls>('/api/calls/missed'),

  /** Everybody waiting for a car right now. */
  held: () => request<HeldCalls>('/api/calls/held'),

  /**
   * Parks this caller to wait for a car, when the lines have room.
   *
   * The API decides and then moves them itself — a desk phone cannot be made to transfer
   * anybody, so the browser no longer does either. `ringBack` moves nobody: the line is one
   * the firm will not keep somebody waiting on, and the dispatcher says so and hangs up.
   */
  hold: (callId: string) =>
    request<HoldDecision>(`/api/calls/${callId}/hold`, { method: 'POST' }),

  recent: () => request<CallSummary[]>('/api/calls'),

  /** Takes responsibility for one, so nobody else rings the same person. */
  take: (callId: string) =>
    request<void>(`/api/calls/${callId}/take`, { method: 'POST' }),

  /** Puts it back, when it turned out not to be dealt with. */
  release: (callId: string) =>
    request<void>(`/api/calls/${callId}/release`, { method: 'POST' }),

  /** Clears it without ringing back. The reason is required. */
  clear: (callId: string, reason: string) =>
    request<void>(`/api/calls/${callId}/clear`, { method: 'POST', body: { reason } }),

  /**
   * Rings a number from the signed-in dispatcher's own telephone.
   *
   * The request carries the number and nothing identifying the caller: the extension is
   * resolved from the token, so this cannot be pointed at somebody else's telephone.
   * Their softphone rings first, then the customer's.
   */
  /**
   * A link to one call's recording — administrators only, and asked for when play is
   * pressed rather than when a list loads: it lasts five minutes, and every one handed out
   * is a line in that recording's trail.
   *
   * `download` signs it to arrive as a file instead, which the trail records as a copy.
   */
  recording: (callId: string, download = false) =>
    request<RecordingLink>(
      `/api/calls/${callId}/recording${download ? '?download=true' : ''}`,
    ),

  /** Everybody who has heard, saved or deleted this call's recording, newest first. */
  trail: (callId: string) =>
    request<RecordingAccess[]>(`/api/calls/${callId}/recording/trail`),

  /** Deletes one recording. The call stays in the log. */
  deleteRecording: (callId: string) =>
    request<void>(`/api/calls/${callId}/recording`, { method: 'DELETE' }),

  /** Deletes every recording of one customer — the request a firm gets by post. */
  forgetCustomer: (customerId: string) =>
    request<{ deleted: number }>(`/api/calls/customer/${customerId}/recordings`, {
      method: 'DELETE',
    }),

  dial: (number: string, inAnswerToCallId?: string) =>
    request<CallSummary>('/api/telephony/calls', {
      method: 'POST',
      body: { number, inAnswerToCallId: inAnswerToCallId ?? null },
    }),
};
